/**
 * OrgFormModal: create / edit Organization modal, on the Carbon Modal with
 * its ProgressIndicator steps (layer-02, the footer button set: Cancel or
 * Back on the left, Next or the save on the right).
 *
 * Two modes:
 *   - create (mode='create'): 5-step stepper: Details / Access / Consent /
 *     Save+Verify / First Scan Readiness. Most ops shipping today only need
 *     the first two; steps 3-5 are informational reminders + handoffs to
 *     existing monitored-tenant flows (still in Phase 4, so we render
 *     conceptual placeholders here, not real flows).
 *   - edit (mode='edit'): 2-step variant, Details + Access. The advanced
 *     steps don't apply to existing rows; the parent of an org doesn't
 *     change in normal operation, and we never re-issue consent links.
 *
 * Slug auto-derivation runs on Name blur using the same kebab-case rule the
 * server expects (^[a-z0-9-]+$). The check goes through `getOrganizationByTid`
 * indirectly — we surface the parent-supplied availability state rather than
 * doing the fetch here, so the modal is decoupled from React Query.
 *
 * The form renders no `<h1>`: the modal title is a heading-level 2, so the
 * page keeps its one h1 (the brief's verdict).
 */

import { useEffect, useMemo, useState } from 'react'

import { Modal } from '@/components/carbon/Modal'
import { NumberInput } from '@/components/carbon/NumberInput'
import { Select } from '@/components/carbon/Select'
import { TextInput } from '@/components/carbon/TextInput'
import { Toggle } from '@/components/carbon/Toggle'

import type {
  CreateOrganizationRequest,
  OrganizationDetailDto,
  OrganizationListItemDto,
  UpdateOrganizationRequest,
} from '@/lib/api'

export type OrgFormMode = 'create' | 'edit'

interface Props {
  open: boolean
  mode: OrgFormMode
  /** Existing org for edit mode — ignored when mode='create'. */
  initial?: OrganizationDetailDto | OrganizationListItemDto | null
  /**
   * Optional seed values for create mode (e.g. tid + name pre-filled from the
   * Add Tenant form's inline detection). Ignored when mode='edit' (use
   * `initial` for that). Fields not present here fall back to EMPTY_FORM
   * defaults — admins still confirm before saving.
   */
  seed?: Partial<{
    name: string
    slug: string
    azureAdTenantId: string
    type: string
    domain: string
  }>
  /** MSP and Platform orgs available as parents — only Customer-type orgs need a parent. */
  msps: OrganizationListItemDto[]
  /** Called when the user saves. Parent runs the mutation. */
  onSave: (
    data: CreateOrganizationRequest | Partial<UpdateOrganizationRequest>,
  ) => void | Promise<void>
  onClose: () => void
  isSaving?: boolean
  /** Slug-availability check — parent fetches against the API and feeds the result. */
  onSlugCheck?: (slug: string) => void
  slugAvailability?: 'unknown' | 'checking' | 'available' | 'taken'
}

interface FormState {
  name: string
  slug: string
  azureAdTenantId: string
  type: string
  parentOrganizationId: string
  primaryContactEmail: string
  primaryContactName: string
  domain: string
  isActive: boolean
  allowSelfSignup: boolean
  allowAutoProvision: boolean
  maxUsers: string
  maxTenants: string
}

const EMPTY_FORM: FormState = {
  name: '',
  slug: '',
  azureAdTenantId: '',
  type: 'Customer',
  parentOrganizationId: '',
  primaryContactEmail: '',
  primaryContactName: '',
  domain: '',
  isActive: true,
  allowSelfSignup: false,
  allowAutoProvision: true,
  maxUsers: '',
  maxTenants: '',
}

/** Kebab-case slug from a name — same rule the server's regex validates against. */
export function deriveSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '')
}

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

export default function OrgFormModal({
  open,
  mode,
  initial,
  seed,
  msps,
  onSave,
  onClose,
  isSaving,
  onSlugCheck,
  slugAvailability,
}: Props) {
  const [step, setStep] = useState(1)
  const totalSteps = mode === 'create' ? 5 : 2
  const [form, setForm] = useState<FormState>(EMPTY_FORM)

  // Initialise / reset on open. The modal can be reused across edit targets;
  // anchoring the seed on `open` + `initial?.id` covers both opening fresh
  // and switching from one edit target to another.
  useEffect(() => {
    if (!open) return
    setStep(1)
    if (mode === 'edit' && initial) {
      setForm({
        name: initial.name ?? '',
        slug: initial.slug ?? '',
        azureAdTenantId: initial.azureAdTenantId ?? '',
        type: initial.type ?? 'Customer',
        parentOrganizationId: initial.parentOrganizationId ?? '',
        primaryContactEmail: initial.primaryContactEmail ?? '',
        primaryContactName: initial.primaryContactName ?? '',
        domain: initial.domain ?? '',
        isActive: initial.isActive ?? true,
        allowSelfSignup: initial.allowSelfSignup ?? false,
        allowAutoProvision: initial.allowAutoProvision ?? false,
        maxUsers: initial.maxUsers != null ? String(initial.maxUsers) : '',
        maxTenants: initial.maxTenants != null ? String(initial.maxTenants) : '',
      })
    } else {
      // Create mode: EMPTY_FORM defaults, then overlay any caller seed values.
      setForm({
        ...EMPTY_FORM,
        ...(seed?.name ? { name: seed.name } : {}),
        ...(seed?.slug ? { slug: seed.slug } : {}),
        ...(seed?.azureAdTenantId ? { azureAdTenantId: seed.azureAdTenantId } : {}),
        ...(seed?.type ? { type: seed.type } : {}),
        ...(seed?.domain ? { domain: seed.domain } : {}),
      })
    }
  }, [open, mode, initial, seed])

  const update = <K extends keyof FormState>(field: K, value: FormState[K]) => {
    setForm(prev => ({ ...prev, [field]: value }))
  }

  // Auto-derive slug on Name blur when slug is empty (don't clobber edits).
  const handleNameBlur = () => {
    if (!form.slug && form.name) {
      const slug = deriveSlug(form.name)
      setForm(prev => ({ ...prev, slug }))
      if (onSlugCheck && slug) onSlugCheck(slug)
    }
  }

  const handleSlugBlur = () => {
    if (onSlugCheck && form.slug) onSlugCheck(form.slug)
  }

  const canAdvance = useMemo(() => {
    if (step === 1) {
      // Required: name, slug, type. tid required only when not Platform.
      if (!form.name.trim() || !form.slug.trim()) return false
      if (!/^[a-z0-9-]+$/.test(form.slug)) return false
      if (form.type === 'Customer' && !form.parentOrganizationId) return false
      return true
    }
    return true
  }, [step, form])

  const handleSubmit = async () => {
    const payload: CreateOrganizationRequest = {
      name: form.name.trim(),
      slug: form.slug.trim(),
      azureAdTenantId: form.azureAdTenantId.trim() || null,
      type: form.type,
      parentOrganizationId: form.parentOrganizationId || null,
      primaryContactEmail: form.primaryContactEmail.trim() || null,
      primaryContactName: form.primaryContactName.trim() || null,
      domain: form.domain.trim() || null,
      isActive: form.isActive,
      allowSelfSignup: form.allowSelfSignup,
      allowAutoProvision: form.allowAutoProvision,
      maxUsers: form.maxUsers ? Number(form.maxUsers) : null,
      maxTenants: form.maxTenants ? Number(form.maxTenants) : null,
    }
    await onSave(payload)
  }

  const isLastStep = step === totalSteps
  const stepIds = mode === 'create' ? CREATE_STEPS : EDIT_STEPS

  return (
    <Modal
      open={open}
      onClose={onClose}
      size={mode === 'create' ? 'lg' : 'md'}
      title={mode === 'create' ? 'Add an organization' : (initial?.name ?? 'Organization')}
      subtitle={`${mode === 'create' ? 'New organization' : 'Edit organization'}. Step ${step} of ${totalSteps}.`}
      steps={stepIds.map(s => ({ id: s.id, label: s.label }))}
      currentStep={stepIds[step - 1]?.id}
      secondaryAction={
        step > 1
          ? { label: 'Back', onClick: () => setStep(s => Math.max(1, s - 1)), disabled: !!isSaving }
          : { label: 'Cancel', onClick: onClose, disabled: !!isSaving }
      }
      primaryAction={
        isLastStep
          ? {
              label: mode === 'create' ? 'Create organization' : 'Save changes',
              onClick: () => void handleSubmit(),
              disabled: !canAdvance,
              loading: !!isSaving,
            }
          : {
              label: 'Next',
              onClick: () => setStep(s => Math.min(totalSteps, s + 1)),
              disabled: !canAdvance,
            }
      }
    >
      {step === 1 && (
        <StepDetails
          form={form}
          msps={msps}
          onChange={update}
          onNameBlur={handleNameBlur}
          onSlugBlur={handleSlugBlur}
          slugAvailability={slugAvailability}
        />
      )}
      {step === 2 && <StepAccess form={form} onChange={update} />}
      {step === 3 && <StepConsentInfo />}
      {step === 4 && <StepSaveAndVerify />}
      {step === 5 && <StepReadiness />}
    </Modal>
  )
}

/* -------------------------------------------------------------------------- */
/* Steps                                                                      */
/* -------------------------------------------------------------------------- */

const CREATE_STEPS = [
  { id: 'details', label: 'Details' },
  { id: 'access', label: 'Access' },
  { id: 'consent', label: 'Consent' },
  { id: 'save', label: 'Save' },
  { id: 'ready', label: 'Ready' },
]

const EDIT_STEPS = CREATE_STEPS.slice(0, 2)

function StepDetails({
  form, msps, onChange, onNameBlur, onSlugBlur, slugAvailability,
}: {
  form: FormState
  msps: OrganizationListItemDto[]
  onChange: <K extends keyof FormState>(field: K, value: FormState[K]) => void
  onNameBlur: () => void
  onSlugBlur: () => void
  slugAvailability?: 'unknown' | 'checking' | 'available' | 'taken'
}) {
  return (
    <div className="grid grid-cols-1 gap-6 c-md:grid-cols-2">
      <TextInput
        id="org-name"
        label="Organization name"
        value={form.name}
        onChange={e => onChange('name', e.target.value)}
        onBlur={onNameBlur}
        placeholder="Acme Corp"
        required
      />

      <TextInput
        id="org-slug"
        label="Slug"
        value={form.slug}
        onChange={e => onChange('slug', e.target.value)}
        onBlur={onSlugBlur}
        placeholder="acme-corp"
        pattern="^[a-z0-9-]+$"
        required
        className="font-mono"
        helper={slugAvailability === 'available' ? 'Slug is available.' : 'Lowercase, digits, and hyphens only.'}
        error={slugAvailability === 'taken' ? 'This slug is already taken.' : undefined}
      />

      <Select
        id="org-type"
        label="Type"
        value={form.type}
        onChange={e => onChange('type', e.target.value)}
        options={[
          { value: 'Customer', label: 'Customer' },
          { value: 'MSP', label: 'MSP' },
          { value: 'Platform', label: 'Platform' },
        ]}
      />

      {form.type === 'Customer' && (
        <Select
          id="org-parent"
          label="Parent organization"
          value={form.parentOrganizationId}
          onChange={e => onChange('parentOrganizationId', e.target.value)}
          required
        >
          <option value="">Select a parent organization</option>
          {msps.map(m => (
            <option key={m.id} value={m.id}>{m.name}</option>
          ))}
        </Select>
      )}

      <TextInput
        id="org-tid"
        label="Microsoft directory ID"
        value={form.azureAdTenantId}
        onChange={e => onChange('azureAdTenantId', e.target.value)}
        placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx"
        helper="Optional until the customer has granted consent."
        className="font-mono"
      />

      <TextInput
        id="org-domain"
        label="Domain"
        value={form.domain}
        onChange={e => onChange('domain', e.target.value)}
        placeholder="acme.com"
      />

      <TextInput
        id="org-contact-name"
        label="Primary contact name"
        value={form.primaryContactName}
        onChange={e => onChange('primaryContactName', e.target.value)}
      />

      <TextInput
        id="org-contact-email"
        type="email"
        label="Primary contact email"
        value={form.primaryContactEmail}
        onChange={e => onChange('primaryContactEmail', e.target.value)}
      />
    </div>
  )
}

function StepAccess({
  form, onChange,
}: {
  form: FormState
  onChange: <K extends keyof FormState>(field: K, value: FormState[K]) => void
}) {
  return (
    <div className="flex flex-col gap-6">
      <Toggle
        label="Let users in on their first sign-in"
        helper="When on, the first user to sign in from this organization is provisioned as a Viewer."
        checked={form.allowAutoProvision}
        onChange={v => onChange('allowAutoProvision', v)}
      />
      <Toggle
        label="Allow self-service registration"
        helper="Lets people from this directory start the onboarding wizard themselves."
        checked={form.allowSelfSignup}
        onChange={v => onChange('allowSelfSignup', v)}
      />

      <div className="grid grid-cols-1 gap-6 c-md:grid-cols-2">
        <NumberInput
          id="org-max-users"
          label="Max users"
          helper="Leave blank for unlimited."
          min={0}
          value={form.maxUsers}
          onChange={e => onChange('maxUsers', e.target.value)}
        />
        <NumberInput
          id="org-max-tenants"
          label="Max monitored environments"
          helper="Leave blank for unlimited."
          min={0}
          value={form.maxTenants}
          onChange={e => onChange('maxTenants', e.target.value)}
        />
      </div>
    </div>
  )
}

// Steps 3-5 are informational placeholders. Phase 4 wires the inline-detection
// flow and Phase 5 wires the signup-gated consent path; until then these read
// as guidance copy so the wizard mental model is in place.
function StepNote({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="bg-layer-1 p-4">
      <h3 className="m-0 text-body font-semibold text-text-primary">{title}</h3>
      <p className="m-0 mt-2 text-body text-text-secondary">{children}</p>
    </section>
  )
}

function StepConsentInfo() {
  return (
    <StepNote title="Consent link">
      When this organization signs in with multi-tenant consent, Guardian generates a one-time link
      for their global admin to grant access. You can send that link from the monitored-environment
      form once the directory ID is set.
    </StepNote>
  )
}

function StepSaveAndVerify() {
  return (
    <StepNote title="Save and verify">
      Saving creates the access record. You will see it appear in the list with a status tag.
      Customer-type organizations start with a status of Active when you create them here directly.
    </StepNote>
  )
}

function StepReadiness() {
  return (
    <StepNote title="First scan readiness">
      After saving, add a monitored environment for this organization from the Monitored Tenants tab.
      The first scan starts within a few minutes of admin consent being granted.
    </StepNote>
  )
}
